/**
 * Mapbox Configuration
 * Centralized configuration for Mapbox with Arabic language support and RTL text plugin
 */

import mapboxgl from 'mapbox-gl';

// Set Mapbox access token from environment
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

// RTL Text Plugin for Arabic text rendering
// This must be set before any map is created
const RTL_PLUGIN_URL = 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js';

let rtlPluginLoaded = false;

/**
 * Initialize the RTL text plugin for Arabic/Hebrew text support
 * This should be called once before creating any maps
 */
export function initializeRTLPlugin(): void {
  if (rtlPluginLoaded) return;
  
  try {
    // Check if plugin is already set
    if (!mapboxgl.getRTLTextPluginStatus || mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
      mapboxgl.setRTLTextPlugin(
        RTL_PLUGIN_URL,
        (error) => {
          if (error) {
            console.error('Error loading Mapbox RTL plugin:', error);
          } else {
            rtlPluginLoaded = true;
          }
        },
        true // lazy load
      );
    } else {
      rtlPluginLoaded = true;
    }
  } catch (error) {
    // Plugin might already be loaded
    console.warn('RTL plugin initialization:', error);
    rtlPluginLoaded = true;
  }
}

/**
 * Arabic map style - uses Mapbox's light style with Arabic labels
 * The localIdeographFontFamily option helps with Arabic font rendering
 */
export const ARABIC_MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

/**
 * Default map options with Arabic language configuration
 */
export const DEFAULT_MAP_OPTIONS: Partial<mapboxgl.MapboxOptions> = {
  style: ARABIC_MAP_STYLE,
  // Use Arabic locale for labels
  locale: {
    'NavigationControl.ZoomIn': 'تكبير',
    'NavigationControl.ZoomOut': 'تصغير',
    'NavigationControl.ResetBearing': 'إعادة تعيين الاتجاه',
  },
};

/**
 * Set the map language to Arabic after the map loads
 * This changes all text labels on the map to Arabic
 */
export function setMapLanguageToArabic(map: mapboxgl.Map): void {
  map.on('load', () => {
    // Get all layers and update text-field for label layers
    const style = map.getStyle();
    if (!style || !style.layers) return;

    style.layers.forEach((layer) => {
      // Only process symbol layers with text
      if (layer.type === 'symbol' && layer.layout && 'text-field' in layer.layout) {
        // Check if the layer has a name_ar or name:ar field available
        // Mapbox styles typically have localized names in format name_<lang> or name:<lang>
        try {
          map.setLayoutProperty(layer.id, 'text-field', [
            'coalesce',
            ['get', 'name_ar'],
            ['get', 'name:ar'],
            ['get', 'name'],
          ]);
        } catch {
          // Some layers might not support this, skip them
        }
      }
    });
  });
}

/**
 * Create a map with Arabic language support
 * This is a helper function that creates a map with all Arabic configurations applied
 */
export function createArabicMap(container: HTMLElement, options: Partial<mapboxgl.MapboxOptions> = {}): mapboxgl.Map {
  // Ensure RTL plugin is initialized
  initializeRTLPlugin();

  const map = new mapboxgl.Map({
    container,
    ...DEFAULT_MAP_OPTIONS,
    ...options,
  });

  // Set language to Arabic after map loads
  setMapLanguageToArabic(map);

  return map;
}

// Initialize RTL plugin on module load
initializeRTLPlugin();

export default mapboxgl;
